import React, { useState, useEffect, useCallback } from 'react';
import { useProfile } from '../../contexts/ProfileContext';
import { authFetch } from '../../utils/auth';
import { exportAllUserData } from '../../utils/dataExport';
import {
  getCategoryLabel,
  getCategoryFilterOptions
} from '../../utils/memoryDisplaySections';
import Button from '../../components/common/Button';
import { FileTextIcon, DownloadIcon, TrashIcon, EditIcon } from '../../components/common/icons';

const MyInformation = () => {
  useProfile();
  const [memories, setMemories] = useState([]);
  const [loading, setLoading] = useState(true);
  // The memory list failed to load: shown as an error with a retry, never as
  // "Ingen minner funnet."
  const [loadError, setLoadError] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showDeleteMemoryConfirm, setShowDeleteMemoryConfirm] = useState(null);
  const [showEditModal, setShowEditModal] = useState(null);
  // Result of the "Slett alle minner" action: 'success' | 'error' | null.
  // Drives the acknowledgment/failure modal so the user gets honest feedback
  // instead of being silently cleared (or silently left untouched on failure).
  const [deleteAllResult, setDeleteAllResult] = useState(null);
  const [exporting, setExporting] = useState(false);
  // Outcome of the export: { type: 'error' } (nothing downloaded) or
  // { type: 'partial', failedSections } (file downloaded, sections missing).
  // null = no notice. Cleared on the next attempt — no silent failures.
  const [exportNotice, setExportNotice] = useState(null);

  // Fetch memories on mount, and again from the error state's "Prøv igjen".
  // (The Familieoversikt and Støttereise cards that previously consumed
  // /memories/grouped have been removed — the grouped fetch went with them.)
  const fetchAllData = useCallback(async () => {
    setLoading(true);
    setLoadError(false);
    try {
      const memoriesResponse = await authFetch('/memories');
      const memoriesData = memoriesResponse.ok
        ? await memoriesResponse.json()
        : null;

      if (memoriesData?.success) {
        setMemories(memoriesData.memories);
      } else {
        setLoadError(true);
      }
    } catch (error) {
      console.error('Error fetching memories:', error);
      setLoadError(true);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAllData();
  }, [fetchAllData]);

  const handleDeleteMemory = async (memoryId) => {
    try {
      const response = await authFetch(`/memories/${memoryId}`, { method: 'DELETE' });
      if (response.ok) {
        setMemories(prev => prev.filter(memory => memory.id !== memoryId));
        setShowDeleteMemoryConfirm(null);
      }
    } catch (error) {
      console.error('Error deleting memory:', error);
    }
  };

  const handleDeleteAllMemories = async () => {
    try {
      const response = await authFetch('/memories/all', {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ confirmed: true })
      });
      if (response.ok) {
        // Verified deletion: clear local state and acknowledge.
        setMemories([]);
        setShowDeleteConfirm(false);
        setDeleteAllResult('success');
      } else {
        // Backend returns non-2xx (e.g. 502) when nothing was deleted. Do NOT
        // clear local state or claim success — surface the failure so the user
        // can retry.
        setShowDeleteConfirm(false);
        setDeleteAllResult('error');
      }
    } catch (error) {
      console.error('Error deleting all memories:', error);
      setShowDeleteConfirm(false);
      setDeleteAllResult('error');
    }
  };

  // Full GDPR export of ALL stores (not just memories) via the shared
  // utility — this page stays a valid entry point, but the scope matches
  // the button label "Eksporter alle data".
  const handleExportData = async () => {
    if (exporting) return;
    setExporting(true);
    setExportNotice(null);
    const result = await exportAllUserData();
    setExporting(false);
    if (result.status === 'error') {
      setExportNotice({ type: 'error' });
    } else if (result.status === 'partial') {
      setExportNotice({ type: 'partial', failedSections: result.failedSections });
    }
  };

  const handleEditMemory = async (memoryId, newContent) => {
    try {
      const response = await authFetch(`/memories/${memoryId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ content: newContent })
      });
      if (response.ok) {
        setMemories(prev => prev.map(memory =>
          memory.id === memoryId ? { ...memory, content: newContent } : memory
        ));
        setShowEditModal(null);
      }
    } catch (error) {
      console.error('Error editing memory:', error);
    }
  };

  const getSensitivityLabel = (level) => {
    switch (level) {
      case 1: return 'Lav';
      case 2: return 'Middels';
      case 3: return 'Høy';
      case 4: return 'Svært høy';
      default: return 'Middels';
    }
  };

  // Single neutral icon used per-memory in the Alle minner list; the previous
  // per-category SVG map collapsed when the icon set was incomplete and not
  // worth maintaining. Category is communicated by the label next to it.
  const categoryIcon = <FileTextIcon className="w-5 h-5 text-primary" />;

  const filteredMemories = memories.filter(memory => {
    const matchesSearch = memory.content.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         memory.entity?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = selectedCategory === 'all' || memory.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  if (loading) {
    return (
      <div className="max-w-6xl mx-auto space-y-8">
        {/* Header skeleton */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center space-y-4 sm:space-y-0">
          <div>
            <div className="skeleton h-8 w-48 mb-2"></div>
            <div className="skeleton h-4 w-64"></div>
          </div>
          <div className="flex space-x-4">
            <div className="skeleton h-10 w-32 rounded-lg"></div>
            <div className="skeleton h-10 w-36 rounded-lg"></div>
          </div>
        </div>

        {/* All memories skeleton */}
        <div className="bg-white rounded-lg shadow-sm border border-neutral-200 p-6">
          <div className="skeleton h-6 w-32 mb-6"></div>
          <div className="space-y-4">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="border border-neutral-200 rounded-lg p-4">
                <div className="flex items-center space-x-2 mb-2">
                  <div className="skeleton-shimmer w-5 h-5 rounded"></div>
                  <div className="skeleton h-4 w-32"></div>
                </div>
                <div className="skeleton h-4 w-full mb-2"></div>
                <div className="skeleton h-3 w-48"></div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto space-y-8">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center space-y-4 sm:space-y-0">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-text-primary">Coord minner</h1>
          <p className="text-text-secondary mt-2">Se og rediger Coord genererte minner.</p>
        </div>
        <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-4 w-full sm:w-auto">
          <button
            onClick={handleExportData}
            disabled={exporting}
            className="w-full sm:w-auto px-4 py-2 min-h-[48px] bg-primary text-white rounded-lg hover:bg-primary-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center justify-center"
          >
            <DownloadIcon className="w-4 h-4 mr-2" />
            {exporting ? 'Eksporterer …' : 'Eksporter alle data'}
          </button>
          <button
            onClick={() => setShowDeleteConfirm(true)}
            className="w-full sm:w-auto px-4 py-2 min-h-[48px] bg-error text-white rounded-lg hover:bg-error-700 transition-colors inline-flex items-center justify-center"
          >
            <TrashIcon className="w-4 h-4 mr-2" />
            Slett alle minner
          </button>
        </div>
      </div>

      {/* Export failure notice — inline near the button, never silent */}
      {exportNotice && (
        <div className="bg-error-50 border border-error rounded-lg p-4 text-sm text-error-700">
          {exportNotice.type === 'error' ? (
            <p>Kunne ikke eksportere dataene dine. Prøv igjen senere.</p>
          ) : (
            <>
              <p>Filen ble lastet ned, men deler av eksporten feilet og mangler i filen:</p>
              <ul className="list-disc ml-5 mt-2 space-y-1">
                {exportNotice.failedSections.map((section, i) => (
                  <li key={i}>{section}</li>
                ))}
              </ul>
              <p className="mt-2">Prøv igjen senere for å få en komplett eksport.</p>
            </>
          )}
        </div>
      )}

      {/*
        Familieoversikt and Støttereise cards were removed in PR 3.
        - Familieoversikt: duplicated /account/family.
        - Støttereise: superseded by the dashboard/journey view (separate work).
        Alle minner below remains the full-transparency / GDPR control surface
        and now lists every canonical category in its filter dropdown.
      */}

      {/* All Memories with Search and Filter */}
      <div className="bg-white rounded-lg shadow-sm border border-neutral-200 p-6">
        <h2 className="text-xl font-semibold mb-4">Alle minner</h2>

        {/* Search and Filter Controls */}
        <div className="flex flex-col md:flex-row gap-4 mb-6">
          <div className="flex-1">
            <input
              type="text"
              placeholder="Søk i minner..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full px-4 py-2 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
            />
          </div>
          <div className="w-full md:w-48">
            <select
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
              className="w-full px-4 py-2 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
            >
              <option value="all">Alle kategorier</option>
              {getCategoryFilterOptions().map(({ category, label }) => (
                <option key={category} value={category}>{label}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Memory List */}
        <div className="space-y-4">
          {loadError ? (
            <div role="alert" className="text-center py-12">
              <p className="text-text-secondary mb-4">
                Vi fikk ikke hentet minnene dine akkurat nå.
              </p>
              <button
                type="button"
                onClick={fetchAllData}
                className="px-6 py-3 min-h-[48px] bg-primary text-white rounded-lg font-semibold hover:bg-primary-600 transition-colors"
              >
                Prøv igjen
              </button>
            </div>
          ) : filteredMemories.length === 0 ? (
            <div className="text-center py-12 text-text-muted">
              <p>Ingen minner funnet.</p>
            </div>
          ) : (
            filteredMemories.map((memory) => (
              <div key={memory.id} className="border border-neutral-200 rounded-lg p-4 hover:bg-neutral-50 transition-colors">
                <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between space-y-3 sm:space-y-0">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center space-x-2 mb-2">
                      <span className="inline-flex">{categoryIcon}</span>
                      <span className="text-sm font-medium text-text-secondary truncate">
                        {getCategoryLabel(memory.category, memory.subcategory)} • Sensitivitet: {getSensitivityLabel(memory.sensitivityLevel)}
                      </span>
                    </div>
                    <p className="text-text-primary mb-2 text-sm sm:text-base">"{memory.content}"</p>
                    <div className="flex flex-col sm:flex-row sm:items-center sm:space-x-4 text-sm text-text-muted space-y-1 sm:space-y-0">
                      <span>Fra samtale {new Date(memory.timestamp).toLocaleDateString('nb-NO')}</span>
                      {memory.entity && <span className="sm:before:content-['•'] sm:before:mr-2">{memory.entity}</span>}
                    </div>
                  </div>
                  <div className="flex space-x-2 sm:ml-4 flex-shrink-0">
                    <button
                      onClick={() => setShowEditModal(memory)}
                      className="text-sm text-primary hover:text-primary-800 py-1 px-2 rounded hover:bg-primary-50 inline-flex items-center"
                    >
                      <EditIcon className="w-4 h-4 mr-1" />
                      Rediger
                    </button>
                    <button
                      onClick={() => setShowDeleteMemoryConfirm(memory)}
                      className="text-sm text-error hover:text-error-800 py-1 px-2 rounded hover:bg-error-50 inline-flex items-center"
                    >
                      <TrashIcon className="w-4 h-4 mr-1" />
                      Slett
                    </button>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>

        {/* Pagination could be added here */}
        {filteredMemories.length > 0 && (
          <div className="mt-6 flex justify-center">
            <p className="text-sm text-text-muted">
              Viser {filteredMemories.length} av {memories.length} minner
            </p>
          </div>
        )}
      </div>

      {/* Delete All Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold mb-4">Slett alle minner</h3>
            <p className="text-text-secondary mb-6">
              Er du sikker på at du vil slette alle lagrede minner? Denne handlingen kan ikke angres.
            </p>
            <div className="flex justify-end space-x-4">
              <Button
                variant="outline"
                className="min-h-[48px]"
                onClick={() => setShowDeleteConfirm(false)}
              >
                Avbryt
              </Button>
              <Button
                variant="danger"
                className="min-h-[48px]"
                onClick={handleDeleteAllMemories}
              >
                Slett alle
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Delete All Result Modal (success / failure acknowledgment) */}
      {deleteAllResult && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            {deleteAllResult === 'success' ? (
              <>
                <h3 className="text-lg font-semibold mb-4">Alle dine data er nå slettet</h3>
                <p className="text-text-secondary mb-6">
                  Alle dine lagrede minner er fjernet. Du kan trygt begynne på nytt når du vil.
                </p>
              </>
            ) : (
              <>
                <h3 className="text-lg font-semibold mb-4 text-error-700">Sletting feilet</h3>
                <p className="text-text-secondary mb-6">
                  Noe gikk galt – dataene dine ble ikke slettet. Prøv igjen.
                </p>
              </>
            )}
            <div className="flex justify-end">
              <Button
                variant="primary"
                className="min-h-[48px]"
                onClick={() => setDeleteAllResult(null)}
              >
                Lukk
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Single Memory Confirmation Modal */}
      {showDeleteMemoryConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold mb-4">Slett minne</h3>
            <p className="text-text-secondary mb-4">
              Er du sikker på at du vil slette dette minnet?
            </p>
            <div className="bg-neutral-50 p-3 rounded-lg mb-6">
              <p className="text-sm text-text-primary italic">
                "{showDeleteMemoryConfirm.content}"
              </p>
            </div>
            <div className="flex justify-end space-x-4">
              <Button
                variant="outline"
                className="min-h-[48px]"
                onClick={() => setShowDeleteMemoryConfirm(null)}
              >
                Avbryt
              </Button>
              <Button
                variant="danger"
                className="min-h-[48px]"
                onClick={() => handleDeleteMemory(showDeleteMemoryConfirm.id)}
              >
                Slett
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Memory Modal */}
      {showEditModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-2xl w-full mx-4">
            <h3 className="text-lg font-semibold mb-4">Rediger minne</h3>
            <div className="mb-4">
              <label className="block text-sm font-medium text-text-primary mb-2">
                Innhold:
              </label>
              <textarea
                defaultValue={showEditModal.content}
                className="w-full px-3 py-2 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                rows="4"
                id="editMemoryContent"
              />
            </div>
            <div className="flex justify-end space-x-4">
              <Button
                variant="outline"
                className="min-h-[48px]"
                onClick={() => setShowEditModal(null)}
              >
                Avbryt
              </Button>
              <Button
                variant="primary"
                className="min-h-[48px]"
                onClick={() => {
                  const newContent = document.getElementById('editMemoryContent').value;
                  handleEditMemory(showEditModal.id, newContent);
                }}
              >
                Lagre
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MyInformation;